import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { UserContext } from "../../../pages/UserContext";
import useEditorAccess from "../../../hooks/useEditorAccess.hook";
import applyPublicModeLifecycle from "../utils/publicModeLifecycle";

/**
 * Persist the in-progress evaluation to localStorage as a *scoped crash-recovery copy*.
 *
 * Design (GSAACR-356): the server report draft is the source of truth once one exists;
 * localStorage is only a safety net so brand-new / "unknown product" / (future) anonymous
 * work — which has no server autosave — survives an accidental reload or a silent Login.gov
 * re-auth. To avoid one product's data bleeding into another (the previous single global key
 * behavior), each draft is stored under a key scoped to the user AND the specific draft:
 *
 *   acr_editor_draft:{userScope}:{report_id | product.id | "new"}
 *
 * @see https://github.com/GSA/openacr-editor/blob/main/src/stores/evaluation.js (original pattern)
 */
const STORAGE_PREFIX = "acr_editor_draft";
/** Pre-scoping single-key names, cleaned up on load so stale global drafts can't resurface. */
const LEGACY_STORAGE_NAME = "acr_editor_store";
const LEGACY_PERSIST_VERSION_KEY = `${LEGACY_STORAGE_NAME}-persist-version`;
/** Bump when the persisted payload contract changes incompatibly; older payloads are discarded. */
const PERSIST_VERSION = "2";
/** Drafts older than this are discarded on read/sweep so abandoned work doesn't linger forever. */
const DRAFT_TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30 days
/** Debounce so a typed sentence produces ~one write, not one per keystroke. */
const WRITE_DEBOUNCE_MS = 400;
/**
 * Per-tab (sessionStorage) pointer to the localStorage key of the draft being actively edited.
 * It survives a page *refresh* (sessionStorage persists across reloads of the same tab) but is
 * cleared when the provider unmounts via in-app navigation. That difference lets us silently
 * rehydrate in-progress work on refresh — the reported "refresh clears the ACR" bug — while still
 * opening on clean defaults (with an explicit About restore banner) when the user intentionally
 * leaves the editor and comes back.
 */
const SESSION_ACTIVE_SCOPE_KEY = "acr_editor_active_scope";

function safeLocalStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    // Access can throw in some privacy modes.
    return null;
  }
}

function safeSessionStorage() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readActiveScopeKey() {
  const storage = safeSessionStorage();
  if (!storage) return null;
  try {
    return storage.getItem(SESSION_ACTIVE_SCOPE_KEY);
  } catch {
    return null;
  }
}

function writeActiveScopeKey(key) {
  const storage = safeSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(SESSION_ACTIVE_SCOPE_KEY, key);
  } catch {
    // ignore
  }
}

function clearActiveScopeKey() {
  const storage = safeSessionStorage();
  if (!storage) return;
  try {
    storage.removeItem(SESSION_ACTIVE_SCOPE_KEY);
  } catch {
    // ignore
  }
}

/** Stable per-user namespace for draft keys. Falls back to "anon" for unauthenticated/future flows. */
export function getDraftUserScope(user) {
  return user?.email || user?.vendorId || user?.agencyId || "anon";
}

/**
 * The draft-identity segment of the key. A report id (server draft) wins; otherwise the product
 * id; otherwise "new" for a not-yet-identified draft. Keeps distinct products/reports isolated.
 */
export function getDraftScopeId(evaluation) {
  const reportId = evaluation?.report_id;
  if (reportId != null && String(reportId).trim() !== "") return `report:${String(reportId)}`;
  const productId = evaluation?.product?.id;
  if (productId != null && String(productId).trim() !== "") return `product:${String(productId)}`;
  return "new";
}

function buildStorageKey(userScope, scopeId) {
  return `${STORAGE_PREFIX}:${userScope || "anon"}:${scopeId || "new"}`;
}

function isExpiredPayload(parsed) {
  if (!parsed || typeof parsed !== "object" || parsed.v !== PERSIST_VERSION) return true;
  return typeof parsed.savedAt === "number" && Date.now() - parsed.savedAt > DRAFT_TTL_MS;
}

/**
 * Read a scoped draft payload (evaluation + savedAt) or null, auto-removing expired/invalid entries.
 */
function readDraftPayload(storage, key) {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (isExpiredPayload(parsed)) {
      storage.removeItem(key);
      return null;
    }
    const { evaluation, savedAt } = parsed;
    if (!evaluation || typeof evaluation !== "object") return null;
    return { evaluation, savedAt: typeof savedAt === "number" ? savedAt : null };
  } catch {
    return null;
  }
}

const DRAFT_TEXT_FIELDS = [
  "notes",
  "evaluation_methods_used",
  "legal_disclaimer",
  "repository",
  "feedback",
];

/**
 * Whether a stored draft holds real user work worth offering to restore. Deliberately ignores the
 * auto-prefilled vendor block and the empty catalog scaffold so a freshly-opened editor never
 * surfaces a "restore?" banner for nothing. The chapter scan runs only on mount (once) and while a
 * restore offer is still pending, so it is not on the per-keystroke path.
 */
export function draftHasMeaningfulContent(evaluation) {
  if (!evaluation || typeof evaluation !== "object") return false;
  const product = evaluation.product ?? {};
  if (String(product.id ?? "").trim() !== "") return true;
  if (["name", "version", "description", "category"].some((k) => String(product[k] ?? "").trim() !== "")) {
    return true;
  }
  if (DRAFT_TEXT_FIELDS.some((k) => String(evaluation[k] ?? "").trim() !== "")) return true;
  if (Array.isArray(evaluation.related_acrs) && evaluation.related_acrs.length > 0) return true;
  const chapters = evaluation.chapters ?? {};
  return Object.values(chapters).some((chapter) =>
    (chapter?.criteria ?? []).some((criterion) =>
      (criterion?.components ?? []).some((component) => {
        const adherence = component?.adherence ?? {};
        return (
          String(adherence.level ?? "").trim() !== "" ||
          String(adherence.notes ?? "").trim() !== ""
        );
      })
    )
  );
}

/**
 * Order-independent fingerprint of a draft's *savable* content (product core, about text fields,
 * related ACRs, and chapter responses), ignoring volatile fields like dates and save status. Used
 * to decide whether an on-device copy actually diverges from the server copy: in the same browser
 * the local copy is almost always timestamp-newer, so recovery must compare content, not time.
 */
export function evaluationContentFingerprint(evaluation) {
  if (!evaluation || typeof evaluation !== "object") return "";
  const product = evaluation.product ?? {};
  const responses = [];
  const chapters = evaluation.chapters ?? {};
  Object.keys(chapters).forEach((chapterId) => {
    (chapters[chapterId]?.criteria ?? []).forEach((criterion) => {
      (criterion?.components ?? []).forEach((component) => {
        const level = String(component?.adherence?.level ?? "").trim();
        const notes = String(component?.adherence?.notes ?? "").trim();
        if (level !== "" || notes !== "") {
          responses.push(`${String(criterion?.num ?? "")}|${String(component?.name ?? "")}|${level}|${notes}`);
        }
      });
    });
  });
  responses.sort();
  return JSON.stringify({
    product: {
      id: product.id ?? "",
      name: product.name ?? "",
      version: product.version ?? "",
      description: product.description ?? "",
      category: product.category ?? "",
    },
    notes: evaluation.notes ?? "",
    evaluation_methods_used: evaluation.evaluation_methods_used ?? "",
    legal_disclaimer: evaluation.legal_disclaimer ?? "",
    repository: evaluation.repository ?? "",
    feedback: evaluation.feedback ?? "",
    license: evaluation.license ?? "",
    related_acrs: (evaluation.related_acrs ?? [])
      .map((a) => String(a?.url ?? "").trim())
      .filter(Boolean)
      .sort(),
    responses,
  });
}

function writeDraft(storage, key, evaluation) {
  try {
    const payload = { v: PERSIST_VERSION, savedAt: Date.now(), evaluation };
    storage.setItem(key, JSON.stringify(payload));
  } catch {
    // ignore quota or other storage errors
  }
}

/** Remove the legacy global draft plus any expired/invalid scoped drafts. */
function sweepStaleDrafts(storage) {
  try {
    storage.removeItem(LEGACY_STORAGE_NAME);
    storage.removeItem(LEGACY_PERSIST_VERSION_KEY);
    const toRemove = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(`${STORAGE_PREFIX}:`)) {
        try {
          const parsed = JSON.parse(storage.getItem(key));
          if (isExpiredPayload(parsed)) toRemove.push(key);
        } catch {
          toRemove.push(key);
        }
      }
    }
    toRemove.forEach((k) => storage.removeItem(k));
  } catch {
    // ignore
  }
}

/** Remove a single scoped draft (e.g. after it is confirmed saved/submitted to the server). */
export function clearEditorDraft(userScope, scopeId) {
  const storage = safeLocalStorage();
  if (!storage) return;
  try {
    storage.removeItem(buildStorageKey(userScope, scopeId));
  } catch {
    // ignore
  }
}

/** Remove every editor draft for every scope (e.g. on logout). Also clears the legacy key. */
export function clearAllEditorDrafts() {
  clearActiveScopeKey();
  const storage = safeLocalStorage();
  if (!storage) return;
  try {
    storage.removeItem(LEGACY_STORAGE_NAME);
    storage.removeItem(LEGACY_PERSIST_VERSION_KEY);
    const toRemove = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(`${STORAGE_PREFIX}:`)) toRemove.push(key);
    }
    toRemove.forEach((k) => storage.removeItem(k));
  } catch {
    // ignore
  }
}

/**
 * Promote every anonymous ("anon"-scoped) draft to an authenticated user's scope.
 *
 * This is the login-time counterpart to {@link clearAllEditorDrafts} (logout): the scoped-storage
 * model (GSAACR-356) always reserved an "anon" namespace for un-authenticated work, and Public Mode
 * (GSAACR-345) is that work actually existing. When a Public Mode author logs in mid-draft, their
 * on-device work must follow them into their own namespace, otherwise the "anon" draft is orphaned
 * (the About restore banner and future entries look under the authenticated scope) and looks lost.
 *
 * A meaningful draft the user already owns under the target scope is never overwritten by a
 * different anonymous draft. An empty placeholder at that key (the editor persists a blank
 * evaluation after an authenticated visit, and a 401 clears the session without clearing drafts)
 * must not block adoption. When this tab was editing the anonymous draft and the owned copy is
 * different real work, the anonymous draft becomes the live copy and the owned one is parked at
 * `prior:{scopeId}` for the restore banner — deleting the anon key and repointing at the older
 * draft would drop the Public Mode work with no offer. Idempotent and a no-op when there is
 * nothing anonymous to move.
 */
function evaluationFromStoredRaw(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || isExpiredPayload(parsed)) return null;
    if (!parsed.evaluation || typeof parsed.evaluation !== "object") return null;
    return parsed.evaluation;
  } catch {
    return null;
  }
}

function storedDraftIsMeaningful(raw) {
  const evaluation = evaluationFromStoredRaw(raw);
  return Boolean(evaluation && draftHasMeaningfulContent(evaluation));
}

export function promoteDraftsToScope(targetScope) {
  if (!targetScope || targetScope === "anon") return;
  const storage = safeLocalStorage();
  if (!storage) return;
  const anonPrefix = `${STORAGE_PREFIX}:anon:`;
  const activeKey = readActiveScopeKey();
  try {
    const moves = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(anonPrefix)) {
        moves.push({ from: key, scopeId: key.slice(anonPrefix.length) });
      }
    }
    moves.forEach(({ from, scopeId }) => {
      const to = buildStorageKey(targetScope, scopeId);
      const incoming = storage.getItem(from);
      const existing = storage.getItem(to);
      // Missing, expired, or empty owned drafts yield to meaningful anonymous work.
      const existingPayload = existing == null ? null : readDraftPayload(storage, to);
      const existingRemoved = existing != null && storage.getItem(to) == null;
      const existingIsEmpty = Boolean(
        existingPayload && !draftHasMeaningfulContent(existingPayload.evaluation)
      );
      const incomingIsMeaningful = storedDraftIsMeaningful(incoming);
      const incomingEvaluation = evaluationFromStoredRaw(incoming);
      const sameWork = Boolean(
        incomingEvaluation
        && existingPayload?.evaluation
        && evaluationContentFingerprint(incomingEvaluation)
          === evaluationContentFingerprint(existingPayload.evaluation)
      );
      const targetFree = existing == null || existingRemoved || (existingIsEmpty && incomingIsMeaningful);
      let adopted = false;
      if (incoming != null && (targetFree || sameWork)) {
        storage.setItem(to, incoming);
        adopted = true;
      } else if (incomingIsMeaningful && existingPayload && activeKey === from && !sameWork) {
        // This tab was editing the anonymous draft. Keep that work live and park the different
        // owned copy so login does not silently restore the older draft and delete the new one.
        const parkedKey = buildStorageKey(targetScope, `prior:${scopeId}`);
        const currentOwned = storage.getItem(to);
        if (currentOwned != null && storage.getItem(parkedKey) == null) {
          storage.setItem(parkedKey, currentOwned);
          storage.setItem(to, incoming);
          adopted = true;
        }
      }
      // A meaningful anon draft that could not be adopted stays put, and the active pointer stays
      // on it. Dropping it here is what discarded Public Mode work on login.
      if (adopted || !incomingIsMeaningful) {
        storage.removeItem(from);
        if (activeKey === from) writeActiveScopeKey(to);
      }
    });
  } catch {
    // ignore storage errors
  }
}

/** Backwards-compatible alias for existing callers: clears all editor drafts. */
export function clearCache() {
  clearAllEditorDrafts();
}

function mergeStoredWithDefaults(defaultState, stored) {
  const result = { ...defaultState };
  Object.keys(stored)
    .filter((key) => Object.prototype.hasOwnProperty.call(stored, key))
    .forEach((key) => {
      const defVal = defaultState[key];
      const storedVal = stored[key];
      if (Array.isArray(defVal) && Array.isArray(storedVal)) {
        result[key] = storedVal;
      } else if (
        defVal != null &&
        typeof defVal === "object" &&
        !Array.isArray(defVal) &&
        storedVal != null &&
        typeof storedVal === "object" &&
        !Array.isArray(storedVal)
      ) {
        result[key] = { ...defVal, ...storedVal };
      } else {
        result[key] = storedVal;
      }
    });
  return result;
}

/** Matches GSA openacr-editor evaluation shape for About form and report output. */
export const INITIAL_EVALUATION_STATE = {
  report_id: null,
  report_save_status: "idle",
  title: "",
  report_date: "",
  last_modified_date: "",
  version: "1",
  notes: "",
  evaluation_methods_used: "",
  legal_disclaimer: "",
  repository: "",
  feedback: "",
  license: "CC-BY-4.0",
  related_acrs: [],
  about_field_format_errors: {},
  product: {
    name: "",
    version: "",
    description: "",
  },
  author: {
    name: "",
    company_name: "",
    address: "",
    email: "",
    phone: "",
    website: "",
  },
  vendor: {
    name: "",
    company_name: "",
    address: "",
    email: "",
    phone: "",
    website: "",
  },
  chapters: {},
};

export const EvaluationContext = React.createContext({
  evaluation: INITIAL_EVALUATION_STATE,
  setEvaluation: () => {},
  updateProduct: () => {},
  updateVendor: () => {},
  pendingRestore: null,
  restoreDraft: () => {},
  discardDraft: () => {},
  clearPersistedDraft: () => {},
  maybeOfferLocalRecovery: () => {},
  resetEvaluation: () => {},
});

/**
 * Decide the provider's initial evaluation and restore offer at mount.
 *
 * A page *refresh* keeps the per-tab active-scope pointer, so we silently rehydrate the exact
 * draft that was being edited (and suppress the About banner — the work simply reappears). A fresh
 * navigation into the editor has no pointer, so we open on clean defaults and only offer a
 * meaningful "new"-scope draft through the About-page restore banner.
 */
function resolveInitialDraftState(initialState, userScope, defaultState) {
  if (initialState != null) return { evaluation: initialState, pendingRestore: null };
  const storage = safeLocalStorage();
  if (storage) {
    sweepStaleDrafts(storage);
    // If we already know the authenticated user at mount, adopt any anonymous work into their scope
    // before resolving the refresh pointer / restore offer, so both look under the right namespace.
    promoteDraftsToScope(userScope);
  }

  const activeKey = readActiveScopeKey();
  if (storage && activeKey) {
    const payload = readDraftPayload(storage, activeKey);
    if (payload && draftHasMeaningfulContent(payload.evaluation)) {
      const evaluation = mergeStoredWithDefaults(defaultState, payload.evaluation);
      // A different owned draft parked during promotion stays available as a restore offer.
      // Its scope id is not "new", so the "user started typing" heuristic does not dismiss it.
      const parked = readDraftPayload(
        storage,
        buildStorageKey(userScope, `prior:${getDraftScopeId(evaluation)}`)
      );
      const pendingRestore = parked && draftHasMeaningfulContent(parked.evaluation)
        ? { ...parked, scopeId: `prior:${getDraftScopeId(evaluation)}` }
        : null;
      return { evaluation, pendingRestore };
    }
    // Pointer is stale (draft expired/emptied) — drop it so we stop treating this as a refresh.
    clearActiveScopeKey();
  }

  let pendingRestore = null;
  if (storage) {
    const payload = readDraftPayload(storage, buildStorageKey(userScope, "new"));
    if (payload && draftHasMeaningfulContent(payload.evaluation)) {
      pendingRestore = { ...payload, scopeId: "new" };
    }
  }
  return { evaluation: defaultState, pendingRestore };
}

export function EvaluationProvider({ children, initialState }) {
  // UserContext can be null in some editor tests/harnesses; read defensively.
  const user = useContext(UserContext)?.user;
  const hasEditorAccess = useEditorAccess();
  const userScope = getDraftUserScope(user);

  const defaultState = useMemo(() => {
    const base = { ...INITIAL_EVALUATION_STATE, catalog: null };
    if (typeof window !== "undefined") {
      const datestamp = new Date().toLocaleDateString();
      return {
        ...base,
        report_date: base.report_date || datestamp,
        last_modified_date: base.last_modified_date || datestamp,
      };
    }
    return base;
  }, []);

  // Resolve the mount-time evaluation + restore offer exactly once. A refresh silently rehydrates
  // the in-progress draft (via the per-tab pointer); a fresh entry opens clean and may offer the
  // "new"-scope draft through the About banner. Computed here so both pieces of state agree.
  const initialResolvedRef = useRef(null);
  if (initialResolvedRef.current === null) {
    initialResolvedRef.current = resolveInitialDraftState(initialState, userScope, defaultState);
  }

  const [evaluation, setEvaluationState] = useState(() => initialResolvedRef.current.evaluation);

  // A recoverable "new"-scope draft (brand-new / unknown-product / future anonymous work that has
  // no server autosave) discovered on mount, surfaced to the About page as a dismissible banner.
  const [pendingRestore, setPendingRestore] = useState(() => initialResolvedRef.current.pendingRestore);

  // Debounced, scoped persistence. The key is derived from the *current* evaluation, so as a
  // draft gains a product/report id its crash-recovery copy follows it to the matching key.
  const writeTimerRef = useRef(null);
  const lastWrittenRef = useRef(null);
  // The latest scheduled-but-not-yet-written draft, so it can be flushed synchronously on unmount /
  // tab close instead of dropped — otherwise the last <400ms of edits are lost when the user leaves
  // the editor via the app nav or closes the tab (GSAACR-356).
  const pendingWriteRef = useRef(null);
  const flushPendingWrite = useCallback(() => {
    const pending = pendingWriteRef.current;
    if (!pending) return;
    if (writeTimerRef.current) {
      clearTimeout(writeTimerRef.current);
      writeTimerRef.current = null;
    }
    if (pending.serialized !== lastWrittenRef.current) {
      writeDraft(pending.storage, pending.key, pending.toStore);
      lastWrittenRef.current = pending.serialized;
    }
    pendingWriteRef.current = null;
  }, []);

  useEffect(() => {
    const storage = safeLocalStorage();
    if (!storage) return undefined;
    const scopeId = getDraftScopeId(evaluation);
    const key = buildStorageKey(userScope, scopeId);
    // Point this tab at the draft being edited so a refresh can restore it. Track only real work
    // (a loaded report/product, or meaningful "new" input) so an empty editor isn't "restored".
    // Done before the pending-restore guard below since this is only a pointer, not a write.
    if (scopeId !== "new" || draftHasMeaningfulContent(evaluation)) {
      writeActiveScopeKey(key);
    }
    // Preserve the copy currently offered for restore: don't overwrite the draft for the scope
    // whose restore banner is still pending (covers both the "new" scope and a reopened report).
    if (pendingRestore && (pendingRestore.scopeId ?? "new") === scopeId) {
      return undefined;
    }
    const toStore = { ...evaluation, last_modified_date: new Date().toLocaleDateString() };
    const serialized = `${key}|${JSON.stringify(toStore)}`;
    if (serialized === lastWrittenRef.current) return undefined; // skip no-op writes
    pendingWriteRef.current = { storage, key, toStore, serialized };
    if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
    writeTimerRef.current = setTimeout(() => {
      writeDraft(storage, key, toStore);
      lastWrittenRef.current = serialized;
      pendingWriteRef.current = null;
      writeTimerRef.current = null;
    }, WRITE_DEBOUNCE_MS);
    // Intentionally no re-render cleanup: each run reschedules by clearing the timer above, and the
    // unmount/tab-close listeners below flush the pending write instead of cancelling it.
    return undefined;
  }, [evaluation, userScope, pendingRestore]);

  // Identity promotion: when the scope resolves to a real user — most importantly when a Public Mode
  // author logs in mid-draft and the scope flips from "anon" — adopt their anonymous on-device work
  // into their own namespace so it keeps restoring under the new identity (the login counterpart to
  // clearing drafts on logout). Fires once per distinct scope; a no-op when nothing is anonymous.
  useEffect(() => {
    if (userScope && userScope !== "anon") {
      promoteDraftsToScope(userScope);
      // Force the next debounced write to reissue under the new-scope key rather than treat the
      // just-migrated content as already-persisted under the old one.
      lastWrittenRef.current = null;
    }
  }, [userScope]);

  // Flush the pending on-device write when the editor unmounts (e.g. navigating to another app
  // section) or the tab is hidden/closed, so the last keystrokes always reach localStorage.
  useEffect(() => flushPendingWrite, [flushPendingWrite]);

  // Clear the per-tab active-scope pointer when the editor unmounts via in-app navigation, so
  // returning later opens on clean defaults (+ About banner). A real page refresh tears the tab
  // down without running this cleanup, so the pointer survives and the reload restores the draft.
  useEffect(() => () => clearActiveScopeKey(), []);
  useEffect(() => {
    const flushNow = () => flushPendingWrite();
    const onVisibilityChange = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") flushPendingWrite();
    };
    window.addEventListener("beforeunload", flushNow);
    window.addEventListener("pagehide", flushNow);
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("beforeunload", flushNow);
      window.removeEventListener("pagehide", flushNow);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [flushPendingWrite]);

  // If the user starts working instead of choosing Restore / Start fresh, retire the offer so
  // their fresh input (now persisted under the same "new" scope) isn't shadowed by a stale prompt.
  // Scoped to the "new" offer only: a reopened-report recovery offer is set right after the server
  // evaluation is committed (always "meaningful"), so applying this heuristic to it would nuke the
  // banner on the same render and defeat the divergent on-device recovery path.
  useEffect(() => {
    if (
      pendingRestore &&
      (pendingRestore.scopeId ?? "new") === "new" &&
      draftHasMeaningfulContent(evaluation)
    ) {
      setPendingRestore(null);
    }
  }, [evaluation, pendingRestore]);

  const setEvaluation = useCallback((newState) => {
    setEvaluationState((prev) =>
      typeof newState === "function" ? newState(prev) : newState
    );
  }, []);

  // Explicit "Restore draft": load the offered on-device draft over clean defaults, then clear it.
  // Reconcile Public Mode here. The editor shell only re-runs that lifecycle when access flips, and
  // a login restore commits the promoted anon draft while access is already granted — otherwise it
  // stays download-only (search UI, autosave off) instead of graduating into Add New.
  const restoreDraft = useCallback(() => {
    if (!pendingRestore?.evaluation) return;
    const merged = mergeStoredWithDefaults(defaultState, pendingRestore.evaluation);
    setEvaluationState(applyPublicModeLifecycle(merged, !hasEditorAccess));
    setPendingRestore(null);
  }, [pendingRestore, defaultState, hasEditorAccess]);

  // Explicit "Start fresh" / "Keep the saved version": drop the offered on-device draft (for either
  // the "new" scope or a reopened report) so it is not offered again, and keep working.
  const discardDraft = useCallback(() => {
    clearEditorDraft(userScope, pendingRestore?.scopeId ?? "new");
    clearActiveScopeKey();
    lastWrittenRef.current = null;
    setPendingRestore(null);
  }, [userScope, pendingRestore]);

  // "Start a new ACR": drop the in-memory draft (product, catalog, chapters) back to clean defaults
  // so the editor behaves as if freshly opened. The previously-open report's server + on-device
  // copies are left intact (it's still resumable from My Draft ACRs) — we only detach this tab from
  // it by clearing the active-scope pointer so a refresh won't reload the report we just left.
  const resetEvaluation = useCallback(() => {
    clearActiveScopeKey();
    lastWrittenRef.current = null;
    setPendingRestore(null);
    setEvaluationState(defaultState);
  }, [defaultState]);

  // Called once the report is safely persisted server-side (e.g. a confirmed submit/complete): the
  // server is now the source of truth, so drop the local crash-recovery copy. We also mark the
  // current evaluation as already-written so the debounced writer treats it as a no-op instead of
  // immediately re-creating the copy — it will persist again only if the user makes further edits.
  const clearPersistedDraft = useCallback(() => {
    const currentScopeId = getDraftScopeId(evaluation);
    clearEditorDraft(userScope, currentScopeId);
    clearEditorDraft(userScope, "new");
    clearActiveScopeKey();
    if (writeTimerRef.current) {
      clearTimeout(writeTimerRef.current);
      writeTimerRef.current = null;
    }
    pendingWriteRef.current = null;
    const key = buildStorageKey(userScope, currentScopeId);
    const toStore = { ...evaluation, last_modified_date: new Date().toLocaleDateString() };
    lastWrittenRef.current = `${key}|${JSON.stringify(toStore)}`;
    setPendingRestore(null);
  }, [evaluation, userScope]);

  // After reopening a server report, offer to restore an on-device copy that diverges from what the
  // server returned (i.e. edits made after the last server save — the exact data-loss case). Reads
  // the local copy synchronously, before the debounced writer rewrites this report's scope, and
  // compares content rather than timestamps (GSAACR-356).
  const maybeOfferLocalRecovery = useCallback(
    (serverEvaluation) => {
      const storage = safeLocalStorage();
      if (!storage) return;
      const scopeId = getDraftScopeId(serverEvaluation);
      if (!scopeId.startsWith("report:")) return;
      const payload = readDraftPayload(storage, buildStorageKey(userScope, scopeId));
      if (!payload || !draftHasMeaningfulContent(payload.evaluation)) return;
      if (
        evaluationContentFingerprint(payload.evaluation) ===
        evaluationContentFingerprint(serverEvaluation)
      ) {
        return;
      }
      setPendingRestore({ evaluation: payload.evaluation, savedAt: payload.savedAt, scopeId });
    },
    [userScope]
  );

  const updateProduct = useCallback((productFields) => {
    setEvaluationState((prev) => ({
      ...prev,
      product: { ...prev.product, ...productFields },
    }));
  }, []);

  const updateVendor = useCallback((vendorFields) => {
    setEvaluationState((prev) => ({
      ...prev,
      vendor: { ...prev.vendor, ...vendorFields },
    }));
  }, []);

  const contextValue = useMemo(
    () => ({
      evaluation,
      setEvaluation,
      updateProduct,
      updateVendor,
      pendingRestore,
      restoreDraft,
      discardDraft,
      clearPersistedDraft,
      maybeOfferLocalRecovery,
      resetEvaluation,
    }),
    [
      evaluation,
      setEvaluation,
      updateProduct,
      updateVendor,
      pendingRestore,
      restoreDraft,
      discardDraft,
      clearPersistedDraft,
      maybeOfferLocalRecovery,
      resetEvaluation,
    ]
  );

  return (
    <EvaluationContext.Provider value={contextValue}>
      {children}
    </EvaluationContext.Provider>
  );
}

EvaluationProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialState: PropTypes.object,
};

EvaluationProvider.defaultProps = {
  initialState: null,
};

export default EvaluationContext;
